import type { TimeSlotSelection, WebinarEvent } from '../types'
import {
  buildOnlineSessionRows,
  formatMeetingDate,
  getOrderedTimeZoneDisplays,
  getWebinarSelectionLabel
} from '../webinar'

type OnlineSessionSlotsProps = {
  selectedIsoDate: string
  selection: TimeSlotSelection | null
  visibleWebinarEvents: WebinarEvent[]
  onSelectTimeSlot: (
    time: string,
    rawTime: string,
    type: 'online',
    eventId?: number,
    eventUrl?: string,
    scheduledStartAt?: string
  ) => void
}

export function OnlineSessionSlots({
  selectedIsoDate,
  selection,
  visibleWebinarEvents,
  onSelectTimeSlot
}: OnlineSessionSlotsProps) {
  if (!selectedIsoDate) {
    return null
  }

  const rows = buildOnlineSessionRows(selectedIsoDate, visibleWebinarEvents)

  return (
    <div
      key={selectedIsoDate}
      className="animate-fade-in-up relative z-0 max-h-[350px] overflow-y-auto overflow-x-hidden pr-1"
    >
      <div className="mb-4 last:mb-0">
        <h4 className="mb-2.5 border-b border-gray-lighter pb-2 text-[13px] font-semibold uppercase tracking-[0.5px] text-primary">
          {formatMeetingDate(selectedIsoDate)}
        </h4>
        <div className="flex flex-col gap-1.5">
          {rows.map((row, index) => {
            const isOnlineSelected =
              selection?.rawTime === row.rawTime && selection?.type === 'online'
            const timeZoneDisplays = getOrderedTimeZoneDisplays(row.event)
            const primaryDisplay = timeZoneDisplays[0]
            const onlineSessionTitle = primaryDisplay?.dayPart || row.event.title || row.time

            return (
              <div
                key={`online-${row.rawTime}`}
                className={`flex items-center justify-between rounded-[10px] bg-gradient-to-r from-[#E65300] to-[#FF7033] px-4 py-3 shadow-[0_4px_16px_rgba(230,83,0,0.25)] transition-all duration-150 ${
                  isOnlineSelected
                    ? 'ring-2 ring-white/40'
                    : 'hover:from-[#cc4900] hover:to-[#e04d00] hover:shadow-[0_6px_20px_rgba(230,83,0,0.4)]'
                }`}
                style={{ animationDelay: `${index * 55}ms` }}
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20">
                    <svg
                      className="h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-bold text-white">{onlineSessionTitle}</span>
                    <p className="text-[12px] font-semibold text-white">
                      Online Information Session
                    </p>
                    <div className="mt-2 space-y-0.5 text-white">
                      {timeZoneDisplays.length > 0 ? (
                        timeZoneDisplays.map(display => (
                          <div
                            key={`${row.event.id}-${display.code}`}
                            className="flex items-center gap-2 text-sm leading-5"
                          >
                            <span className="font-bold">{display.scheduledStartTime}</span>
                            <span className="font-medium text-white/80">
                              {display.abbreviation}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm font-bold leading-5">{row.time}</div>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    onSelectTimeSlot(
                      getWebinarSelectionLabel(row.event),
                      row.rawTime,
                      'online',
                      row.event.id,
                      row.event.publicUrl,
                      row.event.scheduledStartAt
                    )
                  }
                  className={`rounded-[6px] border px-4 py-1.5 text-xs font-bold transition-all duration-150 active:scale-95 ${
                    isOnlineSelected
                      ? 'border-white bg-white text-[#E65300] shadow-sm'
                      : 'border-white/40 bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {isOnlineSelected ? 'Selected ✓' : 'Join Session →'}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
